import { dist } from '@/engine/math';
import { roomAtPx, type Floor } from '@/world/builder';
import type { FloorId } from '@/world/types';
import { boxFree } from './physics';
import type { Game } from './game';
import type { Monster } from './entities';

type PatrolPoint = { floor: FloorId; x: number; y: number };

/** Fixed room/door approaches, never the actor's position or hiding furniture. */
export function nearbyPatrolPoints(g: Game): PatrolPoint[] {
  const c = g.leader;
  if (!c || !['free', 'hidden', 'groggy'].includes(c.state)) return [];
  const f = g.floorOf(c), room = roomAtPx(f, c.x, c.y);
  if (!room) return [];
  const adjacent = new Set(f.doors.filter(d => d.rooms.includes(room.id)).flatMap(d => d.rooms).filter(id => id !== room.id));
  const points: PatrolPoint[] = [];
  const consider = (x: number, y: number) => {
    const r = roomAtPx(f, x, y);
    if (!r || r.id === room.id || r.kind === 'cell' || !boxFree(f, x, y, d => d.kind !== 'exit', 6, 4)) return;
    if (dist(c.x, c.y, x, y) > 260) return;
    // A waiting companion or a hidden friend also gets the same breathing room.
    if ([...g.chars.values()].some(a => a.floor === f.id && ['free', 'hidden', 'groggy'].includes(a.state) && dist(a.x, a.y, x, y) < 180)) return;
    points.push({ floor: f.id, x, y });
  };
  // The grid is anchored to architecture, so moving within a room does not make
  // the destination follow the character pixel for pixel.
  const sample = (floor: Floor, adjacentOnly: boolean) => {
    for (const r of floor.rooms) {
      if (r.id === room.id || adjacentOnly && !adjacent.has(r.id)) continue;
      for (let y = r.y + 1; y < r.y + r.h; y += 4) {
        for (let x = r.x + 1; x < r.x + r.w; x += 4) consider(x * 16 + 8, y * 16 + 8);
      }
    }
  };
  sample(f, true);
  if (!points.length) sample(f, false);
  return points;
}

/** Vary quiet/exposed periods without giving the monster extra senses. */
export function updatePacing(g: Game, m: Monster, dt: number): void {
  const c = g.leader;
  if (!c || !['free', 'hidden', 'groggy'].includes(c.state)) return;
  m.pressureCooldown = Math.max(0, m.pressureCooldown - dt);
  const nearby = !m.transit && c.floor === m.floor && dist(c.x, c.y, m.x, m.y) < 300;
  if (nearby || !['patrol', 'lair'].includes(m.state) || m.carrying.length) {
    m.quietTime = 0;
    m.pressureCooldown = Math.max(m.pressureCooldown, 45);
    return;
  }
  m.quietTime += dt;
  if (m.quietTime < 55 || m.pressureCooldown > 0 || m.transit) return;
  const candidates = nearbyPatrolPoints(g);
  if (!candidates.length) { m.pressureCooldown = 15; return; }
  m.poi = g.rng.pick(candidates);
  m.path = null; m.passagePlan = null; m.passageCheck = 0;
  m.wait = 0; m.state = 'patrol';
  m.quietTime = 0; m.pressureCooldown = 90;
}
